import StickyTableResponsive from '../../common/StickyTableResponsive';
import SortableTh from '../../common/SortableTh';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTableSort } from '../../../hooks/useTableSort';
import {
  AdminActionButton,
  AdminDeactivateModal,
  AdminDeleteModal,
  AdminPeriodFields,
  AdminValidityActions,
  AdminValidityBadge,
  defaultAdminPeriod,
} from './KkpAdminShared';
import AdminBulkPriceAdjustPanel from '../../admin/AdminBulkPriceAdjustPanel';
import AdminFormLabel from '../../admin/AdminFormLabel';
import SearchableSelect from '../../SearchableSelect';
import KkpPlanningMagInlineCell from '../KkpPlanningMagInlineCell';
import { formatEurCell } from '../../../utils/adminBulkPriceAdjust';
import { ADMIN_FIELD_HELP } from '../../../utils/adminHelpContent';
import {
  classifyPlanDeComSupport,
  PDC_SUPPORT_TYPE_GROUPS,
  planDeComSupportTypeKey,
} from '../../../utils/planDeComSupportUtils';
import clientAnnuaireService from '../../../services/clientAnnuaireService';
import kkpService from '../../../services/kkpService';
import pdcService from '../../../services/pdcService';
import { notify } from '../../common/AppToast';
import { AppButton, AppInput, AppModal, AppPillSwitch, AppSpinner } from '../../ui';
import {
  buildProduitRefLabelMap,
  buildProduitRefOptions,
} from '../../../utils/adminProduitRefOptions';

const TARIF_SORT = {
  libelle: { type: 'string', getValue: (row) => row.libelle },
  valeur: { type: 'number', getValue: (row) => row.valeur_numerique ?? 0 },
  ordre: { type: 'number', getValue: (row) => row.ordre ?? 0 },
  validite: { type: 'string', getValue: (row) => row.validity_label },
};

const FRAIS_TECHNIQUES_CATEGORIE = 'frais';
const PACK_ASSO_CATEGORIE = 'pack_asso';
const SUPPORT_TARIF_CATEGORIES = new Set(['produit_print', 'produit_web']);

const tarifSupportTypeKey = (tarif) => {
  // Types de campagne / remises : pas des supports Mag-Set-Web-News (évite MONO WEB → filtre Web).
  if (!SUPPORT_TARIF_CATEGORIES.has(tarif.categorie)) return 'unknown';
  return planDeComSupportTypeKey(
    classifyPlanDeComSupport(tarif.libelle, tarif.categorie),
  );
};

const filterTarifsBySupport = (tarifs, filter) => {
  if (filter === 'all') return tarifs;
  if (filter === 'autres') {
    return tarifs.filter((t) => tarifSupportTypeKey(t) === 'unknown');
  }
  return tarifs.filter((t) => tarifSupportTypeKey(t) === filter);
};

const formatTarifValeurCell = (tarif) => {
  const raw = tarif.effective_price ?? tarif.valeur_numerique;
  if (raw === null || raw === undefined || raw === '') return '—';
  const n = Number(raw);
  if (!Number.isFinite(n)) return '—';

  if (tarif.categorie === 'type_campagne') {
    return (
      <>
        {n.toLocaleString('fr-FR')}
        <span className="d-block text-xs text-secondary-light">code calcul</span>
      </>
    );
  }
  if (tarif.categorie === 'remise_volume' || tarif.categorie === 'remise_volume_news') {
    const pct = Math.abs(n) <= 1 ? n * 100 : n;
    return (
      <>
        {pct.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}&nbsp;%
        <span className="d-block text-xs text-secondary-light">remise</span>
      </>
    );
  }
  return (
    <>
      {formatEurCell(raw)}
      {tarif.price_source === 'dolibarr_mirror' && (
        <span className="d-block text-xs text-secondary-light">miroir</span>
      )}
    </>
  );
};

const PRODUIT_SORT = {
  code: { type: 'string', getValue: (row) => row.code },
  libelle: { type: 'string', getValue: (row) => row.libelle_long },
  validite: { type: 'string', getValue: (row) => row.validity_label },
};

const buildActiveTarifBulkItems = (tarifs, { supportOnly = false } = {}) => tarifs
  .filter((t) => {
    if (supportOnly && !SUPPORT_TARIF_CATEGORIES.has(t.categorie)) return false;
    const price = t.effective_price ?? t.valeur_numerique;
    return t.validity_status === 'active' && price != null && Number(price) > 0;
  })
  .map((t) => ({
    id: String(t.id),
    groupLabel: t.libelle,
    groupSublabel: t.product_ref
      ? `${t.categorie} · ${t.product_ref}${t.price_source === 'dolibarr_mirror' ? ' (miroir)' : ''}`
      : t.categorie,
    value: Number(t.effective_price ?? t.valeur_numerique),
    unit: '€',
  }));

const emptyTarifForm = () => ({
  id: '', categorie: '', libelle: '', valeur_numerique: '', product_ref: '', ordre: 0,
  valid_from_year: new Date().getFullYear(),
  valid_from_month: new Date().getMonth() + 1,
});

const emptyFraisForm = () => ({
  id: '', libelle: '', valeur_numerique: '', ordre: 0,
  product_ref: '', product_qty: null,
  valid_from_year: new Date().getFullYear(),
  valid_from_month: new Date().getMonth() + 1,
});

const emptyProduitForm = () => ({
  id: '', code: '', libelle_long: '',
  valid_from_year: new Date().getFullYear(),
  valid_from_month: new Date().getMonth() + 1,
});

const KkpAdminChiffrageSection = () => {
  const [moisOptions, setMoisOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [chiffrage, setChiffrage] = useState({ tarifs: [], produits: [] });
  const [catalogueProduits, setCatalogueProduits] = useState([]);
  const [pricePushEnabled, setPricePushEnabled] = useState(false);
  const [chiffrageTab, setChiffrageTab] = useState('tarifs');
  const [tarifSupportFilter, setTarifSupportFilter] = useState('all');
  const [tarifBulkOpen, setTarifBulkOpen] = useState(false);
  const [fraisBulkOpen, setFraisBulkOpen] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [tarifForm, setTarifForm] = useState(emptyTarifForm);
  const [tarifModalOpen, setTarifModalOpen] = useState(false);
  const [fraisForm, setFraisForm] = useState(emptyFraisForm);
  const [fraisModalOpen, setFraisModalOpen] = useState(false);
  const [produitForm, setProduitForm] = useState(emptyProduitForm);
  const [produitModalOpen, setProduitModalOpen] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivatePeriod, setDeactivatePeriod] = useState(defaultAdminPeriod);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteContextKey, setDeleteContextKey] = useState(null);

  const chiffrageTarifs = useMemo(
    () => chiffrage.tarifs.filter((item) => item.categorie !== FRAIS_TECHNIQUES_CATEGORIE && item.categorie !== PACK_ASSO_CATEGORIE),
    [chiffrage.tarifs],
  );
  const chiffrageFrais = useMemo(
    () => chiffrage.tarifs.filter((item) => item.categorie === FRAIS_TECHNIQUES_CATEGORIE),
    [chiffrage.tarifs],
  );
  const filteredChiffrageTarifs = useMemo(
    () => filterTarifsBySupport(chiffrageTarifs, tarifSupportFilter),
    [chiffrageTarifs, tarifSupportFilter],
  );
  const tarifFilterCounts = useMemo(() => {
    const counts = { all: chiffrageTarifs.length, mag: 0, set: 0, web: 0, newsletter: 0, autres: 0 };
    chiffrageTarifs.forEach((t) => {
      const key = tarifSupportTypeKey(t);
      if (key === 'unknown') counts.autres += 1;
      else if (counts[key] != null) counts[key] += 1;
    });
    return counts;
  }, [chiffrageTarifs]);
  const tarifFilterOptions = useMemo(() => {
    const options = [
      { value: 'all', label: 'Tous', count: tarifFilterCounts.all },
      ...PDC_SUPPORT_TYPE_GROUPS.map((group) => ({
        value: group.key,
        label: group.key === 'newsletter' ? 'News' : group.label,
        count: tarifFilterCounts[group.key] ?? 0,
      })),
    ];
    if (tarifFilterCounts.autres > 0) {
      options.push({ value: 'autres', label: 'Autres', count: tarifFilterCounts.autres });
    }
    return options;
  }, [tarifFilterCounts]);
  const { sortedRows: sortedTarifs, sort: sortTarifs, toggleSort: toggleTarifs, getSortIcon: getTarifsIcon } = useTableSort(filteredChiffrageTarifs, TARIF_SORT);
  const { sortedRows: sortedFrais, sort: sortFrais, toggleSort: toggleFrais, getSortIcon: getFraisIcon } = useTableSort(chiffrageFrais, TARIF_SORT);
  const { sortedRows: sortedProduits, sort: sortProduits, toggleSort: toggleProduits, getSortIcon: getProduitsIcon } = useTableSort(chiffrage.produits, PRODUIT_SORT);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tarifsPayload, produits, planning, catalogue] = await Promise.all([
        pdcService.listAdminTarifs(),
        pdcService.listAdminProduitsBdc(),
        kkpService.listPlanningAdmin(),
        clientAnnuaireService.listProduits(),
      ]);
      setChiffrage({ tarifs: tarifsPayload.tarifs, produits });
      setPricePushEnabled(Boolean(tarifsPayload.price_push_enabled));
      setMoisOptions(planning.mois ?? []);
      setCatalogueProduits(catalogue);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const produitRefOptions = useMemo(
    () => buildProduitRefOptions(
      catalogueProduits,
      [
        tarifForm.product_ref,
        ...chiffrage.tarifs.map((t) => t.product_ref),
      ],
    ),
    [catalogueProduits, chiffrage.tarifs, tarifForm.product_ref],
  );

  const produitRefLabelByValue = useMemo(
    () => buildProduitRefLabelMap(produitRefOptions),
    [produitRefOptions],
  );

  const wrapSave = async (fn, successMessage) => {
    setSaving(true);
    try {
      await fn();
      if (successMessage) notify.success(successMessage);
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setSaving(true);
    try {
      if (deactivateTarget.kind === 'tarif') {
        await pdcService.deactivateAdminTarif(deactivateTarget.id, deactivatePeriod);
      } else if (deactivateTarget.kind === 'produit') {
        await pdcService.deactivateAdminProduitBdc(deactivateTarget.id, deactivatePeriod);
      }
      setDeactivateTarget(null);
      notify.success('Option désactivée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      if (deleteContextKey === 'tarif') {
        await pdcService.deleteAdminTarif(deleteTarget.id);
      } else if (deleteContextKey === 'produit') {
        await pdcService.deleteAdminProduitBdc(deleteTarget.id);
      }
      setDeleteTarget(null);
      setDeleteContextKey(null);
      notify.success('Élément supprimé');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (item, contextKey = null) => {
    setDeleteTarget(item);
    setDeleteContextKey(contextKey);
  };

  const resetTarifForm = () => setTarifForm(emptyTarifForm());
  const resetFraisForm = () => setFraisForm(emptyFraisForm());
  const resetProduitForm = () => setProduitForm(emptyProduitForm());

  const closeTarifModal = () => {
    setTarifModalOpen(false);
    resetTarifForm();
  };

  const openTarifCreateModal = () => {
    resetTarifForm();
    setTarifModalOpen(true);
  };

  const openTarifEditModal = (t) => {
    setTarifForm({
      id: t.id,
      categorie: t.categorie,
      libelle: t.libelle,
      valeur_numerique: t.effective_price ?? t.valeur_numerique ?? '',
      product_ref: t.product_ref ?? '',
      ordre: t.ordre ?? 0,
      valid_from_year: t.valid_from_year ?? new Date().getFullYear(),
      valid_from_month: t.valid_from_month ?? 1,
    });
    setTarifModalOpen(true);
  };

  const submitTarifForm = () => {
    wrapSave(
      () => pdcService.upsertAdminTarif({
        id: tarifForm.id || undefined,
        categorie: tarifForm.categorie,
        libelle: tarifForm.libelle,
        valeur_numerique: tarifForm.valeur_numerique !== '' ? Number(tarifForm.valeur_numerique) : null,
        product_ref: tarifForm.product_ref || null,
        ordre: Number(tarifForm.ordre) || 0,
        valid_from_year: tarifForm.valid_from_year,
        valid_from_month: tarifForm.valid_from_month,
      }).then(() => closeTarifModal()),
      tarifForm.id ? 'Tarif enregistré' : 'Tarif ajouté',
    );
  };

  const closeFraisModal = () => {
    setFraisModalOpen(false);
    resetFraisForm();
  };

  const closeProduitModal = () => {
    setProduitModalOpen(false);
    resetProduitForm();
  };

  const openFraisCreateModal = () => {
    resetFraisForm();
    setFraisModalOpen(true);
  };

  const openProduitCreateModal = () => {
    resetProduitForm();
    setProduitModalOpen(true);
  };

  const openFraisEditModal = (t) => {
    setFraisForm({
      id: t.id,
      libelle: t.libelle,
      valeur_numerique: t.effective_price ?? t.valeur_numerique ?? '',
      product_ref: t.product_ref ?? '',
      product_qty: t.product_qty ?? null,
      ordre: t.ordre ?? 0,
      valid_from_year: t.valid_from_year ?? new Date().getFullYear(),
      valid_from_month: t.valid_from_month ?? 1,
    });
    setFraisModalOpen(true);
  };

  const openProduitEditModal = (p) => {
    setProduitForm({
      id: p.id,
      code: p.code,
      libelle_long: p.libelle_long,
      valid_from_year: p.valid_from_year ?? new Date().getFullYear(),
      valid_from_month: p.valid_from_month ?? 1,
    });
    setProduitModalOpen(true);
  };

  const submitFraisForm = () => {
    wrapSave(
      () => pdcService.upsertAdminTarif({
        id: fraisForm.id || undefined,
        categorie: FRAIS_TECHNIQUES_CATEGORIE,
        libelle: fraisForm.libelle,
        valeur_numerique: fraisForm.valeur_numerique !== '' ? Number(fraisForm.valeur_numerique) : null,
        product_ref: fraisForm.product_ref || null,
        product_qty: fraisForm.product_qty ?? null,
        ordre: Number(fraisForm.ordre) || 0,
        valid_from_year: fraisForm.valid_from_year,
        valid_from_month: fraisForm.valid_from_month,
      }).then(() => closeFraisModal()),
      fraisForm.id ? 'Frais technique enregistré' : 'Frais technique ajouté',
    );
  };

  const submitProduitForm = () => {
    wrapSave(
      () => pdcService.upsertAdminProduitBdc({
        id: produitForm.id || undefined,
        code: produitForm.code,
        libelle_long: produitForm.libelle_long,
        valid_from_year: produitForm.valid_from_year,
        valid_from_month: produitForm.valid_from_month,
      }).then(() => closeProduitModal()),
      produitForm.id ? 'Produit enregistré' : 'Produit ajouté',
    );
  };

  const clearInlineEdit = () => {
    setEditingCell(null);
    setEditDraft(null);
  };

  const switchChiffrageTab = (tab) => {
    clearInlineEdit();
    closeTarifModal();
    closeFraisModal();
    closeProduitModal();
    setFraisBulkOpen(false);
    setChiffrageTab(tab);
  };

  const findChiffrageInlineRow = (venteId, entity) => {
    const id = String(venteId);
    if (entity === 'produit') {
      return chiffrage.produits.find((p) => String(p.id) === id) ?? null;
    }
    return chiffrage.tarifs.find((t) => String(t.id) === id) ?? null;
  };

  const getChiffrageInlineDraft = (row, field, entity) => {
    if (!row) return '';
    if (entity === 'produit') {
      if (field === 'code') return row.code ?? '';
      if (field === 'libelle_long') return row.libelle_long ?? '';
      return '';
    }
    if (field === 'categorie') return row.categorie ?? '';
    if (field === 'libelle') return row.libelle ?? '';
    if (field === 'valeur_numerique') return row.effective_price ?? row.valeur_numerique ?? '';
    if (field === 'product_ref') return String(row.product_ref ?? '').toUpperCase();
    if (field === 'ordre') return row.ordre ?? 0;
    return '';
  };

  const normalizeChiffrageInlineDraft = (field, draft) => {
    if (field === 'valeur_numerique') {
      if (draft === '' || draft === null || draft === undefined) return null;
      return Number(draft);
    }
    if (field === 'ordre') return Number(draft) || 0;
    if (field === 'product_ref') {
      const ref = typeof draft === 'string' ? draft.trim() : String(draft ?? '').trim();
      return ref !== '' ? ref.toUpperCase() : null;
    }
    return typeof draft === 'string' ? draft.trim() : draft;
  };

  const startChiffrageInlineEdit = async (venteId, field, entity = 'tarif') => {
    if (!venteId || !field) {
      clearInlineEdit();
      return;
    }
    if (
      editingCell
      && (editingCell.venteId !== venteId
        || editingCell.field !== field
        || editingCell.entity !== entity)
    ) {
      await saveChiffrageInlineEdit();
    }
    const row = findChiffrageInlineRow(venteId, entity);
    if (!row) return;
    setEditingCell({ venteId, field, entity });
    setEditDraft(getChiffrageInlineDraft(row, field, entity));
  };

  const saveChiffrageInlineEdit = async (draftOverride) => {
    if (!editingCell || inlineSaving) return;
    const { venteId, field, entity } = editingCell;
    const row = findChiffrageInlineRow(venteId, entity);
    if (!row) {
      clearInlineEdit();
      return;
    }

    const draft = draftOverride !== undefined ? draftOverride : editDraft;
    const original = getChiffrageInlineDraft(row, field, entity);
    const normalizedDraft = normalizeChiffrageInlineDraft(field, draft);
    const normalizedOriginal = normalizeChiffrageInlineDraft(field, original);
    if (String(normalizedDraft ?? '') === String(normalizedOriginal ?? '')) {
      clearInlineEdit();
      return;
    }

    if (entity === 'produit') {
      if (field === 'code' && !String(normalizedDraft ?? '').trim()) {
        notify.error('Le code ne peut pas être vide.');
        return;
      }
      if (field === 'libelle_long' && !String(normalizedDraft ?? '').trim()) {
        notify.error('Le libellé ne peut pas être vide.');
        return;
      }
    } else {
      if (field === 'categorie' && !String(normalizedDraft ?? '').trim()) {
        notify.error('La catégorie ne peut pas être vide.');
        return;
      }
      if (field === 'libelle' && !String(normalizedDraft ?? '').trim()) {
        notify.error('Le libellé ne peut pas être vide.');
        return;
      }
    }

    setInlineSaving(true);
    try {
      if (entity === 'produit') {
        await pdcService.upsertAdminProduitBdc({
          id: row.id,
          code: field === 'code' ? normalizedDraft : row.code,
          libelle_long: field === 'libelle_long' ? normalizedDraft : row.libelle_long,
          valid_from_year: row.valid_from_year,
          valid_from_month: row.valid_from_month,
        });
        setChiffrage((prev) => ({
          ...prev,
          produits: prev.produits.map((p) => (
            String(p.id) === String(row.id) ? { ...p, [field]: normalizedDraft } : p
          )),
        }));
      } else {
        await pdcService.upsertAdminTarif({
          id: row.id,
          categorie: field === 'categorie' ? normalizedDraft : row.categorie,
          libelle: field === 'libelle' ? normalizedDraft : row.libelle,
          valeur_numerique: field === 'valeur_numerique' ? normalizedDraft : row.valeur_numerique,
          product_ref: field === 'product_ref' ? normalizedDraft : (row.product_ref ?? null),
          product_qty: row.product_qty ?? null,
          ordre: field === 'ordre' ? normalizedDraft : (row.ordre ?? 0),
          valid_from_year: row.valid_from_year,
          valid_from_month: row.valid_from_month,
        });
        setChiffrage((prev) => ({
          ...prev,
          tarifs: prev.tarifs.map((t) => {
            if (String(t.id) !== String(row.id)) return t;
            const next = { ...t, [field]: normalizedDraft };
            if (field === 'valeur_numerique') {
              next.effective_price = normalizedDraft;
            }
            return next;
          }),
        }));
      }
      clearInlineEdit();
      notify.success('Modification enregistrée');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setInlineSaving(false);
    }
  };

  const pdcTarifBulkItems = useMemo(
    () => buildActiveTarifBulkItems(filteredChiffrageTarifs, { supportOnly: true }),
    [filteredChiffrageTarifs],
  );
  const pdcFraisBulkItems = useMemo(
    () => buildActiveTarifBulkItems(chiffrageFrais),
    [chiffrageFrais],
  );

  const applyPdcTarifBulkUpdates = async (updates, tarifList) => {
    const byId = Object.fromEntries(updates.map((u) => [u.id, u.newValue]));
    await Promise.all(
      tarifList
        .filter((t) => byId[String(t.id)] != null)
        .map((t) => pdcService.upsertAdminTarif({
          id: t.id,
          categorie: t.categorie,
          libelle: t.libelle,
          valeur_numerique: byId[String(t.id)],
          product_ref: t.product_ref ?? null,
          product_qty: t.product_qty ?? null,
          ordre: t.ordre ?? 0,
          valid_from_year: t.valid_from_year,
          valid_from_month: t.valid_from_month,
        })),
    );
    const tarifsPayload = await pdcService.listAdminTarifs();
    setChiffrage((prev) => ({ ...prev, tarifs: tarifsPayload.tarifs }));
    setPricePushEnabled(Boolean(tarifsPayload.price_push_enabled));
    notify.success(
      pricePushEnabled
        ? `${updates.length} tarif${updates.length > 1 ? 's' : ''} mis à jour (local + Dolibarr)`
        : `${updates.length} tarif${updates.length > 1 ? 's' : ''} mis à jour en local (non poussés vers Dolibarr)`,
    );
  };

  const handlePdcTarifsBulkApply = async (updates) => {
    setSaving(true);
    try {
      await applyPdcTarifBulkUpdates(updates, chiffrageTarifs);
    } catch (err) {
      notify.error(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handlePdcFraisBulkApply = async (updates) => {
    setSaving(true);
    try {
      await applyPdcTarifBulkUpdates(updates, chiffrageFrais);
    } catch (err) {
      notify.error(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AppSpinner />;

  return (
    <>
      <AppPillSwitch
        className="mb-3"
        ariaLabel="Onglets chiffrage"
        value={chiffrageTab}
        onChange={switchChiffrageTab}
        options={[
          { value: 'tarifs', label: 'Tarifs', icon: 'mdi:currency-eur' },
          { value: 'frais-techniques', label: 'Frais techniques', icon: 'mdi:wrench-outline' },
          { value: 'produits', label: 'Produits BDC', icon: 'mdi:package-variant-closed' },
        ]}
      />

      {chiffrageTab === 'tarifs' && (
        <>
          <div className="card admin-data-card">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div className="d-flex flex-wrap align-items-center gap-3">
                <span>Grille tarifs ({sortedTarifs.length})</span>
                <AppPillSwitch
                  ariaLabel="Filtrer les tarifs par support"
                  value={tarifSupportFilter}
                  onChange={(value) => {
                    setTarifSupportFilter(value);
                    setTarifBulkOpen(false);
                  }}
                  options={tarifFilterOptions}
                />
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <AppButton
                  size="sm"
                  variant={tarifBulkOpen ? 'outline-secondary' : 'danger-600'}
                  icon={tarifBulkOpen ? 'mdi:chevron-up' : 'mdi:calculator-variant'}
                  disabled={saving || inlineSaving || pdcTarifBulkItems.length === 0}
                  onClick={() => setTarifBulkOpen((open) => !open)}
                >
                  {tarifBulkOpen ? 'Masquer' : 'Augmenter les prix…'}
                </AppButton>
                <AppButton
                  size="sm"
                  variant="danger-600"
                  icon="mdi:plus"
                  disabled={saving || inlineSaving}
                  onClick={openTarifCreateModal}
                >
                  Ajouter
                </AppButton>
              </div>
            </div>
            <AdminBulkPriceAdjustPanel
              hideHeader
              expanded={tarifBulkOpen}
              onExpandedChange={setTarifBulkOpen}
              title="Augmenter les tarifs Plan de Com en groupe"
              description="Uniquement les tarifs actifs visibles dans le filtre courant."
              items={pdcTarifBulkItems}
              onApply={handlePdcTarifsBulkApply}
              disabled={saving}
              dolibarrPushEnabled={pricePushEnabled}
            />
            <StickyTableResponsive>
              <table className="table table-hover table-sm mb-0">
                <thead><tr>
                  <SortableTh sortKey="libelle" label="Libellé" sort={sortTarifs} onSort={toggleTarifs} getSortIcon={getTarifsIcon} />
                  <SortableTh sortKey="valeur" label="Tarif" sort={sortTarifs} onSort={toggleTarifs} getSortIcon={getTarifsIcon} />
                  <th>Réf. Dolibarr</th>
                  <SortableTh sortKey="ordre" label="Ordre" sort={sortTarifs} onSort={toggleTarifs} getSortIcon={getTarifsIcon} />
                  <SortableTh sortKey="validite" label="Validité" sort={sortTarifs} onSort={toggleTarifs} getSortIcon={getTarifsIcon} />
                  <SortableTh label="Actions" className="text-end" sortable={false} />
                </tr></thead>
                <tbody>
                  {sortedTarifs.map((t) => (
                    <tr key={t.id} className={t.validity_status !== 'active' ? 'opacity-50' : ''}>
                      <KkpPlanningMagInlineCell
                        venteId={t.id}
                        field="libelle"
                        type="text"
                        editingCell={editingCell?.entity === 'tarif' ? editingCell : null}
                        editDraft={editDraft}
                        onEditDraftChange={setEditDraft}
                        onStartEdit={(id, field) => startChiffrageInlineEdit(id, field, 'tarif')}
                        onSave={saveChiffrageInlineEdit}
                        saving={inlineSaving}
                      >
                        {t.libelle}
                      </KkpPlanningMagInlineCell>
                      <KkpPlanningMagInlineCell
                        venteId={t.id}
                        field="valeur_numerique"
                        type="number"
                        inputStep="any"
                        editingCell={editingCell?.entity === 'tarif' ? editingCell : null}
                        editDraft={editDraft}
                        onEditDraftChange={setEditDraft}
                        onStartEdit={(id, field) => startChiffrageInlineEdit(id, field, 'tarif')}
                        onSave={saveChiffrageInlineEdit}
                        saving={inlineSaving}
                        className="text-end"
                      >
                        {formatTarifValeurCell(t)}
                      </KkpPlanningMagInlineCell>
                      <KkpPlanningMagInlineCell
                        venteId={t.id}
                        field="product_ref"
                        type="searchable-select"
                        options={produitRefOptions}
                        allowEmpty
                        emptyLabel="— Aucune —"
                        editingCell={editingCell?.entity === 'tarif' ? editingCell : null}
                        editDraft={editDraft}
                        onEditDraftChange={setEditDraft}
                        onStartEdit={(id, field) => startChiffrageInlineEdit(id, field, 'tarif')}
                        onSave={saveChiffrageInlineEdit}
                        saving={inlineSaving}
                        className="text-sm"
                      >
                        {produitRefLabelByValue.get(String(t.product_ref || '').toUpperCase()) || t.product_ref || '—'}
                      </KkpPlanningMagInlineCell>
                      <KkpPlanningMagInlineCell
                        venteId={t.id}
                        field="ordre"
                        type="number"
                        editingCell={editingCell?.entity === 'tarif' ? editingCell : null}
                        editDraft={editDraft}
                        onEditDraftChange={setEditDraft}
                        onStartEdit={(id, field) => startChiffrageInlineEdit(id, field, 'tarif')}
                        onSave={saveChiffrageInlineEdit}
                        saving={inlineSaving}
                      >
                        {t.ordre}
                      </KkpPlanningMagInlineCell>
                      <td>
                        <AdminValidityBadge status={t.validity_status} />
                        <div className="text-xs text-secondary-light mt-1">{t.validity_label}</div>
                      </td>
                      <td className="text-end">
                        <div className="d-flex flex-wrap gap-1 justify-content-end">
                          <AdminActionButton
                            title="Modifier via formulaire"
                            icon="mdi:pencil"
                            className="btn-outline-secondary"
                            disabled={saving || inlineSaving}
                            onClick={() => openTarifEditModal(t)}
                          />
                          <AdminValidityActions
                            item={t}
                            saving={saving || inlineSaving}
                            onDeactivate={(item) => {
                              setDeactivatePeriod(defaultAdminPeriod());
                              setDeactivateTarget({ id: item.id, label: item.libelle, kind: 'tarif' });
                            }}
                            onReactivate={(id) => wrapSave(() => pdcService.reactivateAdminTarif(id), 'Tarif réactivé')}
                            onDelete={(item) => openDeleteModal({ ...item, label: item.libelle }, 'tarif')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </StickyTableResponsive>
          </div>
        </>
      )}

      {chiffrageTab === 'frais-techniques' && (
        <div className="card admin-data-card">
          <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
            <span>Frais techniques ({sortedFrais.length})</span>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <AppButton
                size="sm"
                variant={fraisBulkOpen ? 'outline-secondary' : 'danger-600'}
                icon={fraisBulkOpen ? 'mdi:chevron-up' : 'mdi:calculator-variant'}
                disabled={saving || inlineSaving || pdcFraisBulkItems.length === 0}
                onClick={() => setFraisBulkOpen((open) => !open)}
              >
                {fraisBulkOpen ? 'Masquer' : 'Augmenter les prix…'}
              </AppButton>
              <AppButton
                size="sm"
                variant="danger-600"
                icon="mdi:plus"
                disabled={saving || inlineSaving}
                onClick={openFraisCreateModal}
              >
                Ajouter
              </AppButton>
            </div>
          </div>
          <AdminBulkPriceAdjustPanel
            hideHeader
            expanded={fraisBulkOpen}
            onExpandedChange={setFraisBulkOpen}
            title="Augmenter les frais techniques en groupe"
            description="Rédaction, déplacements, bannières… — tous les frais techniques actifs."
            items={pdcFraisBulkItems}
            onApply={handlePdcFraisBulkApply}
            disabled={saving}
            dolibarrPushEnabled={pricePushEnabled}
          />
          <StickyTableResponsive>
            <table className="table table-hover table-sm mb-0">
              <thead><tr>
                <SortableTh sortKey="libelle" label="Libellé" sort={sortFrais} onSort={toggleFrais} getSortIcon={getFraisIcon} />
                <SortableTh sortKey="valeur" label="Tarif" sort={sortFrais} onSort={toggleFrais} getSortIcon={getFraisIcon} />
                <SortableTh sortKey="ordre" label="Ordre" sort={sortFrais} onSort={toggleFrais} getSortIcon={getFraisIcon} />
                <SortableTh sortKey="validite" label="Validité" sort={sortFrais} onSort={toggleFrais} getSortIcon={getFraisIcon} />
                <SortableTh label="Actions" className="text-end" sortable={false} />
              </tr></thead>
              <tbody>
                {sortedFrais.map((t) => (
                  <tr key={t.id} className={t.validity_status !== 'active' ? 'opacity-50' : ''}>
                    <KkpPlanningMagInlineCell
                      venteId={t.id}
                      field="libelle"
                      type="text"
                      editingCell={editingCell?.entity === 'tarif' ? editingCell : null}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={(id, field) => startChiffrageInlineEdit(id, field, 'tarif')}
                      onSave={saveChiffrageInlineEdit}
                      saving={inlineSaving}
                    >
                      {t.libelle}
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={t.id}
                      field="valeur_numerique"
                      type="number"
                      inputStep="any"
                      editingCell={editingCell?.entity === 'tarif' ? editingCell : null}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={(id, field) => startChiffrageInlineEdit(id, field, 'tarif')}
                      onSave={saveChiffrageInlineEdit}
                      saving={inlineSaving}
                      className="text-end"
                    >
                      {formatTarifValeurCell(t)}
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={t.id}
                      field="ordre"
                      type="number"
                      editingCell={editingCell?.entity === 'tarif' ? editingCell : null}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={(id, field) => startChiffrageInlineEdit(id, field, 'tarif')}
                      onSave={saveChiffrageInlineEdit}
                      saving={inlineSaving}
                    >
                      {t.ordre}
                    </KkpPlanningMagInlineCell>
                    <td>
                      <AdminValidityBadge status={t.validity_status} />
                      <div className="text-xs text-secondary-light mt-1">{t.validity_label}</div>
                    </td>
                    <td className="text-end">
                      <div className="d-flex flex-wrap gap-1 justify-content-end">
                        <AdminActionButton
                          title="Modifier via formulaire"
                          icon="mdi:pencil"
                          className="btn-outline-secondary"
                          disabled={saving || inlineSaving}
                          onClick={() => openFraisEditModal(t)}
                        />
                        <AdminValidityActions
                          item={t}
                          saving={saving || inlineSaving}
                          onDeactivate={(item) => {
                            setDeactivatePeriod(defaultAdminPeriod());
                            setDeactivateTarget({ id: item.id, label: item.libelle, kind: 'tarif' });
                          }}
                          onReactivate={(id) => wrapSave(() => pdcService.reactivateAdminTarif(id), 'Frais technique réactivé')}
                          onDelete={(item) => openDeleteModal({ ...item, label: item.libelle }, 'tarif')}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StickyTableResponsive>
        </div>
      )}

      {chiffrageTab === 'produits' && (
        <div className="card admin-data-card">
          <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
            <span>Produits BDC ({sortedProduits.length})</span>
            <AppButton
              size="sm"
              variant="danger-600"
              icon="mdi:plus"
              disabled={saving || inlineSaving}
              onClick={openProduitCreateModal}
            >
              Ajouter
            </AppButton>
          </div>
          <StickyTableResponsive>
            <table className="table table-hover table-sm mb-0">
              <thead><tr>
                <SortableTh sortKey="code" label="Code" sort={sortProduits} onSort={toggleProduits} getSortIcon={getProduitsIcon} />
                <SortableTh sortKey="libelle" label="Libellé long" sort={sortProduits} onSort={toggleProduits} getSortIcon={getProduitsIcon} />
                <SortableTh sortKey="validite" label="Validité" sort={sortProduits} onSort={toggleProduits} getSortIcon={getProduitsIcon} />
                <SortableTh label="Actions" className="text-end" sortable={false} />
              </tr></thead>
              <tbody>
                {sortedProduits.map((p) => (
                  <tr key={p.id} className={p.validity_status !== 'active' ? 'opacity-50' : ''}>
                    <KkpPlanningMagInlineCell
                      venteId={p.id}
                      field="code"
                      type="text"
                      editingCell={editingCell?.entity === 'produit' ? editingCell : null}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={(id, field) => startChiffrageInlineEdit(id, field, 'produit')}
                      onSave={saveChiffrageInlineEdit}
                      saving={inlineSaving}
                    >
                      {p.code}
                    </KkpPlanningMagInlineCell>
                    <KkpPlanningMagInlineCell
                      venteId={p.id}
                      field="libelle_long"
                      type="text"
                      editingCell={editingCell?.entity === 'produit' ? editingCell : null}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={(id, field) => startChiffrageInlineEdit(id, field, 'produit')}
                      onSave={saveChiffrageInlineEdit}
                      saving={inlineSaving}
                      className="small"
                    >
                      {p.libelle_long}
                    </KkpPlanningMagInlineCell>
                    <td>
                      <AdminValidityBadge status={p.validity_status} />
                      <div className="text-xs text-secondary-light mt-1">{p.validity_label}</div>
                    </td>
                    <td className="text-end">
                      <div className="d-flex flex-wrap gap-1 justify-content-end">
                        <AdminActionButton
                          title="Modifier via formulaire"
                          icon="mdi:pencil"
                          className="btn-outline-secondary"
                          disabled={saving || inlineSaving}
                          onClick={() => openProduitEditModal(p)}
                        />
                        <AdminValidityActions
                          item={p}
                          saving={saving || inlineSaving}
                          onDeactivate={(item) => {
                            setDeactivatePeriod(defaultAdminPeriod());
                            setDeactivateTarget({ id: item.id, label: item.code, kind: 'produit' });
                          }}
                          onReactivate={(id) => wrapSave(() => pdcService.reactivateAdminProduitBdc(id), 'Produit réactivé')}
                          onDelete={(item) => openDeleteModal({ ...item, label: item.code }, 'produit')}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StickyTableResponsive>
        </div>
      )}

      <AppModal
        show={tarifModalOpen}
        title={tarifForm.id ? 'Modifier un tarif' : 'Ajouter un tarif Plan de Com'}
        onClose={closeTarifModal}
        size="lg"
        centered
        contentAs="form"
        contentProps={{
          onSubmit: (e) => {
            e.preventDefault();
            submitTarifForm();
          },
        }}
        footer={(
          <>
            <AppButton type="button" variant="outline-secondary" onClick={closeTarifModal} disabled={saving}>
              Annuler
            </AppButton>
            <AppButton type="submit" variant={tarifForm.id ? 'success' : 'danger-600'} loading={saving}>
              {tarifForm.id ? 'Enregistrer' : 'Ajouter'}
            </AppButton>
          </>
        )}
      >
        <p className="text-secondary-light small mb-3">
          Montant utilisé dans le chiffrage Koikispass
        </p>
        <div className="row g-3">
          <div className="col-md-6">
            <AdminFormLabel help={ADMIN_FIELD_HELP.tarifCategorie.help} required>Catégorie</AdminFormLabel>
            <AppInput required value={tarifForm.categorie} onChange={(e) => setTarifForm((f) => ({ ...f, categorie: e.target.value }))} />
          </div>
          <div className="col-md-6">
            <AdminFormLabel help={ADMIN_FIELD_HELP.tarifLibelle.help} helpExample={ADMIN_FIELD_HELP.tarifLibelle.helpExample} required>Libellé</AdminFormLabel>
            <AppInput required value={tarifForm.libelle} onChange={(e) => setTarifForm((f) => ({ ...f, libelle: e.target.value }))} />
          </div>
          <div className="col-md-4">
            <AdminFormLabel help="Montant HT en euros utilisé dans le chiffrage.">Tarif (€)</AdminFormLabel>
            <AppInput type="number" step="any" value={tarifForm.valeur_numerique} onChange={(e) => setTarifForm((f) => ({ ...f, valeur_numerique: e.target.value }))} />
          </div>
          <div className="col-md-5">
            <AdminFormLabel help={ADMIN_FIELD_HELP.productRef?.help} helpExample={ADMIN_FIELD_HELP.productRef?.helpExample}>
              Réf. Dolibarr
            </AdminFormLabel>
            <SearchableSelect
              value={tarifForm.product_ref}
              onChange={(value) => setTarifForm((f) => ({ ...f, product_ref: value }))}
              options={produitRefOptions}
              allowEmpty
              emptyOptionLabel="— Aucune —"
              placeholder="Produit en vente…"
              emptyLabel="Aucun produit catalogue"
              disabled={saving}
            />
          </div>
          <div className="col-md-3">
            <AdminFormLabel help={ADMIN_FIELD_HELP.tarifOrdre.help} helpExample={ADMIN_FIELD_HELP.tarifOrdre.helpExample}>Ordre</AdminFormLabel>
            <AppInput type="number" value={tarifForm.ordre} onChange={(e) => setTarifForm((f) => ({ ...f, ordre: e.target.value }))} />
          </div>
          <AdminPeriodFields
            year={tarifForm.valid_from_year}
            month={tarifForm.valid_from_month}
            moisOptions={moisOptions}
            onYearChange={(year) => setTarifForm((f) => ({ ...f, valid_from_year: year }))}
            onMonthChange={(month) => setTarifForm((f) => ({ ...f, valid_from_month: month }))}
          />
        </div>
      </AppModal>

      <AppModal
        show={fraisModalOpen}
        title={fraisForm.id ? 'Modifier un frais technique' : 'Ajouter un frais technique'}
        onClose={closeFraisModal}
        size="lg"
        centered
        contentAs="form"
        contentProps={{
          onSubmit: (e) => {
            e.preventDefault();
            submitFraisForm();
          },
        }}
        footer={(
          <>
            <AppButton type="button" variant="outline-secondary" onClick={closeFraisModal} disabled={saving}>
              Annuler
            </AppButton>
            <AppButton type="submit" variant={fraisForm.id ? 'success' : 'danger-600'} loading={saving}>
              {fraisForm.id ? 'Enregistrer' : 'Ajouter'}
            </AppButton>
          </>
        )}
      >
        <div className="row g-3">
          <div className="col-md-12">
            <AdminFormLabel help={ADMIN_FIELD_HELP.fraisLibelle.help} helpExample={ADMIN_FIELD_HELP.fraisLibelle.helpExample} required>Libellé</AdminFormLabel>
            <AppInput required value={fraisForm.libelle} onChange={(e) => setFraisForm((f) => ({ ...f, libelle: e.target.value }))} />
          </div>
          <div className="col-md-4">
            <AdminFormLabel help="Montant HT en euros utilisé dans le chiffrage.">Tarif (€)</AdminFormLabel>
            <AppInput type="number" step="any" value={fraisForm.valeur_numerique} onChange={(e) => setFraisForm((f) => ({ ...f, valeur_numerique: e.target.value }))} />
          </div>
          <div className="col-md-3">
            <AdminFormLabel help={ADMIN_FIELD_HELP.tarifOrdre.help} helpExample={ADMIN_FIELD_HELP.tarifOrdre.helpExample}>Ordre</AdminFormLabel>
            <AppInput type="number" value={fraisForm.ordre} onChange={(e) => setFraisForm((f) => ({ ...f, ordre: e.target.value }))} />
          </div>
          <AdminPeriodFields
            year={fraisForm.valid_from_year}
            month={fraisForm.valid_from_month}
            moisOptions={moisOptions}
            onYearChange={(year) => setFraisForm((f) => ({ ...f, valid_from_year: year }))}
            onMonthChange={(month) => setFraisForm((f) => ({ ...f, valid_from_month: month }))}
          />
        </div>
      </AppModal>

      <AppModal
        show={produitModalOpen}
        title={produitForm.id ? 'Modifier un produit BDC' : 'Ajouter un produit BDC'}
        onClose={closeProduitModal}
        size="lg"
        centered
        contentAs="form"
        contentProps={{
          onSubmit: (e) => {
            e.preventDefault();
            submitProduitForm();
          },
        }}
        footer={(
          <>
            <AppButton type="button" variant="outline-secondary" onClick={closeProduitModal} disabled={saving}>
              Annuler
            </AppButton>
            <AppButton type="submit" variant={produitForm.id ? 'success' : 'danger-600'} loading={saving}>
              {produitForm.id ? 'Enregistrer' : 'Ajouter'}
            </AppButton>
          </>
        )}
      >
        <div className="row g-3">
          <div className="col-md-4">
            <AdminFormLabel help={ADMIN_FIELD_HELP.produitBdcCode.help} helpExample={ADMIN_FIELD_HELP.produitBdcCode.helpExample} required>Code</AdminFormLabel>
            <AppInput required value={produitForm.code} onChange={(e) => setProduitForm((f) => ({ ...f, code: e.target.value }))} />
          </div>
          <div className="col-md-8">
            <AdminFormLabel help={ADMIN_FIELD_HELP.produitBdcLibelle.help} helpExample={ADMIN_FIELD_HELP.produitBdcLibelle.helpExample} required>Libellé long</AdminFormLabel>
            <AppInput required value={produitForm.libelle_long} onChange={(e) => setProduitForm((f) => ({ ...f, libelle_long: e.target.value }))} />
          </div>
          <AdminPeriodFields
            year={produitForm.valid_from_year}
            month={produitForm.valid_from_month}
            moisOptions={moisOptions}
            onYearChange={(year) => setProduitForm((f) => ({ ...f, valid_from_year: year }))}
            onMonthChange={(month) => setProduitForm((f) => ({ ...f, valid_from_month: month }))}
          />
        </div>
      </AppModal>

      <AdminDeactivateModal
        target={deactivateTarget}
        period={deactivatePeriod}
        moisOptions={moisOptions}
        saving={saving}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={handleDeactivate}
        onPeriodChange={setDeactivatePeriod}
      />
      <AdminDeleteModal
        target={deleteTarget}
        contextKey={deleteContextKey}
        saving={saving}
        onClose={() => { setDeleteTarget(null); setDeleteContextKey(null); }}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default KkpAdminChiffrageSection;
